import {
  ArrowPathIcon,
  CheckCircleIcon,
  CircleStackIcon,
  ClockIcon,
  CloudIcon,
  CpuChipIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PlayIcon,
  ServerIcon,
  ShieldCheckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';

interface BackendHealthResponse {
  status: 'healthy' | 'warning' | 'critical' | 'error';
  score: number;
  checks: {
    database: string;
    last_self_check: string | null;
    open_errors: number;
    active_tasks: number;
    error?: string;
  };
  timestamp: string;
}

interface SystemMetrics {
  project: {
    name: string;
    status: string;
    age_days: number;
  };
  tasks: {
    total: number;
    completed: number;
    active: number;
    backlog: number;
    blocked: number;
    completion_rate: number;
  };
  errors: {
    total: number;
    open: number;
    resolved: number;
    resolution_rate: number;
  };
  runs: {
    total: number;
    successful: number;
    failed: number;
    success_rate: number;
    avg_duration_seconds: number;
  };
  health: {
    last_self_check_score: number | null;
    last_self_check_at: string | null;
  };
  timestamp: string;
}

const EnvHealthPage: React.FC = () => {
  const [autoRefresh, setAutoRefresh] = useState(true);

  const {
    data: healthData,
    isLoading: healthLoading,
    error: healthError,
  } = useQuery<BackendHealthResponse>({
    queryKey: ['tracker-health'],
    queryFn: async (): Promise<BackendHealthResponse> => {
      const response = await apiService.get('/tracker/health');
      return response as BackendHealthResponse;
    },
    refetchInterval: autoRefresh ? 30000 : false,
  });

  const {
    data: metrics,
    isLoading: metricsLoading,
    error: metricsError,
  } = useQuery<SystemMetrics>({
    queryKey: ['tracker-metrics'],
    queryFn: async (): Promise<SystemMetrics> => {
      const response = await apiService.get('/tracker/metrics');
      return response as SystemMetrics;
    },
    refetchInterval: autoRefresh ? 30000 : false,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-50';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50';
      case 'unhealthy':
        return 'text-red-600 bg-red-50';
      case 'unknown':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon className="h-5 w-5" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5" />;
      case 'unhealthy':
        return <XCircleIcon className="h-5 w-5" />;
      case 'unknown':
        return <InformationCircleIcon className="h-5 w-5" />;
      default:
        return <InformationCircleIcon className="h-5 w-5" />;
    }
  };

  const getServiceIcon = (service: string) => {
    const serviceLower = service.toLowerCase();
    if (
      serviceLower.includes('database') ||
      serviceLower.includes('postgres') ||
      serviceLower.includes('mysql')
    ) {
      return <CircleStackIcon className="h-5 w-5" />;
    }
    if (serviceLower.includes('redis') || serviceLower.includes('cache')) {
      return <ServerIcon className="h-5 w-5" />;
    }
    if (serviceLower.includes('cpu') || serviceLower.includes('memory')) {
      return <CpuChipIcon className="h-5 w-5" />;
    }
    if (
      serviceLower.includes('cloud') ||
      serviceLower.includes('aws') ||
      serviceLower.includes('azure')
    ) {
      return <CloudIcon className="h-5 w-5" />;
    }
    if (serviceLower.includes('security') || serviceLower.includes('auth')) {
      return <ShieldCheckIcon className="h-5 w-5" />;
    }
    return <ServerIcon className="h-5 w-5" />;
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getHealthSummary = () => {
    if (!healthData) return { total: 0, healthy: 0, warning: 0, unhealthy: 0 };

    // Convert backend health checks to frontend format
    const checks = [
      {
        service: 'Database',
        status: healthData.checks.database === 'healthy' ? 'healthy' : ('unhealthy' as const),
        message: `Database connection is ${healthData.checks.database}`,
      },
      {
        service: 'Error Monitoring',
        status: healthData.checks.open_errors > 0 ? 'warning' : ('healthy' as const),
        message: `${healthData.checks.open_errors} open errors`,
      },
      {
        service: 'Task Management',
        status: healthData.checks.active_tasks > 10 ? 'warning' : ('healthy' as const),
        message: `${healthData.checks.active_tasks} active tasks`,
      },
    ];

    const summary = checks.reduce(
      (acc: { total: number; healthy: number; warning: number; unhealthy: number }, check) => {
        acc.total++;
        switch (check.status) {
          case 'healthy':
            acc.healthy++;
            break;
          case 'warning':
            acc.warning++;
            break;
          case 'unhealthy':
            acc.unhealthy++;
            break;
        }
        return acc;
      },
      { total: 0, healthy: 0, warning: 0, unhealthy: 0 }
    );

    return summary;
  };

  const healthSummary = getHealthSummary();

  if (healthLoading || metricsLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (healthError || metricsError) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading environment health</h3>
              <p className="mt-2 text-sm text-red-700">
                {healthError instanceof Error ? healthError.message : 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Environment Health</h1>
          <p className="text-gray-600">Monitor system health and performance metrics</p>
        </div>
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <span className="ml-2 text-sm text-gray-700">Auto-refresh</span>
          </label>
          <button
            onClick={() => {
              window.location.reload();
            }}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Health Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Healthy Services</p>
              <p className="text-2xl font-semibold text-gray-900">{healthSummary.healthy}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Warnings</p>
              <p className="text-2xl font-semibold text-gray-900">{healthSummary.warning}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <XCircleIcon className="h-8 w-8 text-red-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Unhealthy</p>
              <p className="text-2xl font-semibold text-gray-900">{healthSummary.unhealthy}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ServerIcon className="h-8 w-8 text-blue-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Services</p>
              <p className="text-2xl font-semibold text-gray-900">{healthSummary.total}</p>
            </div>
          </div>
        </div>
      </div>

      {/* System Metrics */}
      {metrics && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Metrics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
                <span className="text-sm font-medium text-gray-600">Task Completion</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {metrics.tasks.completion_rate?.toFixed(1) ?? 'N/A'}%
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{ width: `${Math.min(metrics.tasks.completion_rate ?? 0, 100)}%` }}
                ></div>
              </div>
            </div>

            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <ClockIcon className="h-6 w-6 text-blue-500 mr-2" />
                <span className="text-sm font-medium text-gray-600">Active Tasks</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {metrics.tasks.active ?? 'N/A'}
              </div>
              <div className="text-xs text-gray-500 mt-1">of {metrics.tasks.total ?? 0} total</div>
            </div>

            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <XCircleIcon className="h-6 w-6 text-red-500 mr-2" />
                <span className="text-sm font-medium text-gray-600">Open Errors</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{metrics.errors.open ?? 'N/A'}</div>
              <div className="text-xs text-gray-500 mt-1">of {metrics.errors.total ?? 0} total</div>
            </div>

            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <PlayIcon className="h-6 w-6 text-purple-500 mr-2" />
                <span className="text-sm font-medium text-gray-600">Run Success Rate</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {metrics.runs.success_rate?.toFixed(1) ?? 'N/A'}%
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {metrics.runs.successful ?? 0} of {metrics.runs.total ?? 0}
              </div>
            </div>
          </div>

          <div className="mt-4 text-xs text-gray-500 text-center">
            Last updated: {formatTimestamp(metrics.timestamp)}
          </div>
        </div>
      )}

      {/* Health Checks */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Service Health Checks</h2>
        </div>
        <ul className="divide-y divide-gray-200">
          {healthData &&
            [
              {
                id: 'database',
                service: 'Database',
                status:
                  healthData.checks.database === 'healthy' ? 'healthy' : ('unhealthy' as const),
                message: `Database connection is ${healthData.checks.database}`,
                response_time: null,
                last_checked: healthData.timestamp,
                details: null,
                tenant_id: '',
              },
              {
                id: 'errors',
                service: 'Error Monitoring',
                status: healthData.checks.open_errors > 0 ? 'warning' : ('healthy' as const),
                message: `${healthData.checks.open_errors} open errors`,
                response_time: null,
                last_checked: healthData.timestamp,
                details: { open_errors: healthData.checks.open_errors },
                tenant_id: '',
              },
              {
                id: 'tasks',
                service: 'Task Management',
                status: healthData.checks.active_tasks > 10 ? 'warning' : ('healthy' as const),
                message: `${healthData.checks.active_tasks} active tasks`,
                response_time: null,
                last_checked: healthData.timestamp,
                details: { active_tasks: healthData.checks.active_tasks },
                tenant_id: '',
              },
            ].map((check) => (
              <li key={check.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="flex-shrink-0">{getServiceIcon(check.service)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{check.service}</p>
                      <p className="text-sm text-gray-600">{check.message}</p>
                      <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                        <span>Last checked: {formatTimestamp(check.last_checked)}</span>
                        {check.response_time && <span>Response time: {check.response_time}ms</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(check.status)}`}
                    >
                      {getStatusIcon(check.status)}
                      <span className="ml-1 capitalize">{check.status}</span>
                    </div>
                  </div>
                </div>

                {/* Details */}
                {check.details && Object.keys(check.details).length > 0 && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-md">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Details</h4>
                    <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                      {JSON.stringify(check.details, null, 2)}
                    </pre>
                  </div>
                )}
              </li>
            ))}
        </ul>

        {!healthData && (
          <div className="text-center py-12">
            <ServerIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No health checks available</h3>
            <p className="mt-1 text-sm text-gray-500">
              Health monitoring services are not configured yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnvHealthPage;
